/**
 * Единая точка правды о хостах сайта и филиале запроса.
 * Конфиг (env) — в ./site-host-env, здесь — разбор запроса и решение proxy.js.
 */
import {
    DEFAULT_BRANCH_CODE,
    branchCodeForHost,
    getBranchHost,
    getBranchRouting,
    getPublicHost,
    getPublicHosts,
    getSiteHostConfig,
    isHostRoutedBranch,
    isLocalHost,
    normalizeHost,
    siteUrlForBranch,
} from './site-host-env';
import { getBranchCodeFromPath, isValidBranchCode, regionalizePath, removeRegionPrefix } from './branches-config';

export {
    DEFAULT_BRANCH_CODE,
    branchCodeForHost,
    getBranchHost,
    getBranchRouting,
    getCookieDomain,
    getPublicHost,
    getPublicHosts,
    getSiteHostConfig,
    isHostRoutedBranch,
    isLocalHost,
    isPublicHost,
    normalizeHost,
    readSiteHostConfig,
    setClientSiteHostConfig,
    siteUrlForBranch,
} from './site-host-env';
export type { BranchRouting, SiteHostConfig } from './site-host-env';

export interface ResolvedBranch {
    /** Код филиала: spb, msk */
    code: string;
    /** Путь так, как его видит посетитель (в режиме host — без /geo/<code>) */
    publicPath: string;
    /** Хост филиала (куда ведут канонические ссылки) */
    host: string;
}

/**
 * Филиал запроса:
 *  (a) хост совпадает с хостом филиала (BRANCH_ROUTING=host) — этот филиал, путь без префикса;
 *  (b) путь начинается с /geo/<code>/ — филиал из пути (в режиме host так выглядит
 *      только внутренний путь после rewrite в proxy.js, поэтому правило действует в обоих режимах);
 *  (c) иначе — филиал по умолчанию.
 */
export function resolveBranch({ host, pathname }: { host?: string | null; pathname?: string | null }): ResolvedBranch {
    const path = pathname || '/';
    const hostCode = branchCodeForHost(host);
    if (hostCode && isValidBranchCode(hostCode)) {
        return { code: hostCode, publicPath: removeRegionPrefix(path), host: getBranchHost(hostCode) };
    }
    const code = getBranchCodeFromPath(path);
    return {
        code,
        publicPath: isHostRoutedBranch(code) ? removeRegionPrefix(path) : path,
        host: getBranchHost(code),
    };
}

/** Абсолютный URL пути в филиале: https://<host филиала><путь филиала>. */
export function branchAbsoluteUrl(path: string, branchCode: string = DEFAULT_BRANCH_CODE): string {
    return `${siteUrlForBranch(branchCode)}${regionalizePath(path, branchCode)}`;
}

/** Публичный хост запроса: первый из Host / X-Forwarded-Host, который мы знаем. */
export function requestPublicHost(headers: { get(name: string): string | null }): string {
    const candidates = [headers.get('host'), headers.get('x-forwarded-host')].map(normalizeHost).filter(Boolean);
    const known = new Set([...getPublicHosts(), ...Object.values(getSiteHostConfig().branchHosts)]);
    return candidates.find((host) => known.has(host)) || candidates[0] || '';
}

/**
 * Страницы без региональной версии (живут только в дереве app/(spb)).
 * В режиме path на них ведут редиректы /geo/:geo/... из next.config.js,
 * в режиме host на хосте филиала proxy.js отправляет 301 на главный хост.
 */
const DEFAULT_BRANCH_ONLY: Array<{ pattern: RegExp; branchParam?: boolean }> = [
    { pattern: /^\/raschet-(?:zdaniya|konstruktsii|ferm)\/?$/, branchParam: true },
    { pattern: /^\/pages\/(?:o-kompanii|dostavka)\/?$/ },
];

/**
 * Служебные страницы дерева app/(spb), которые на хосте филиала рендерятся как
 * есть (без rewrite в /geo/<code>): филиал они берут из заголовка x-branch.
 */
const PASSTHROUGH = [/^\/spasibo\/?$/, /^\/shop\/order-success\/?$/, /^\/vizitka(?:\/|$)/, /^\/_next\//, /^\/\.well-known\//];

function isFilePath(pathname: string): boolean {
    const last = pathname.substring(pathname.lastIndexOf('/') + 1);
    return last.includes('.');
}

export type BranchRouteDecision =
    | { type: 'next'; branch: string; publicHost: string; publicPath: string }
    | { type: 'rewrite'; branch: string; publicHost: string; publicPath: string; internalPath: string }
    | { type: 'redirect'; location: string }
    | { type: 'notFound' };

/**
 * Решение proxy.js для хоста и пути запроса.
 * Режим path: всегда 'next' (поведение не меняется, только вычисляется филиал).
 * Режим host:
 *  - хост филиала + /p           -> rewrite /geo/<code>/p (публичный путь /p);
 *  - хост филиала + /geo/<x>/p    -> 301 на чистый путь хоста филиала <x>;
 *  - главный хост + /geo/<code>/p -> 301 на https://<хост code>/p, если у code свой хост;
 *  - неизвестный хост             -> 404 (localhost/127.0.0.1 считаются главным хостом).
 */
export function routeBranchRequest({ host, pathname, search = '' }: { host: string; pathname: string; search?: string }): BranchRouteDecision {
    const normalizedHost = normalizeHost(host);
    const path = pathname || '/';

    if (getBranchRouting() !== 'host') {
        const resolved = resolveBranch({ host: normalizedHost, pathname: path });
        return { type: 'next', branch: resolved.code, publicHost: normalizedHost || getPublicHost(), publicPath: path };
    }

    const hostBranch = branchCodeForHost(normalizedHost);
    const geoMatch = path.match(/^\/geo\/([^/]+)(\/.*)?$/);

    if (hostBranch) {
        if (geoMatch && isValidBranchCode(geoMatch[1])) {
            const target = regionalizePath(geoMatch[2] || '/', geoMatch[1], hostBranch);
            const location = /^https?:\/\//.test(target) ? target : `https://${getBranchHost(hostBranch)}${target}`;
            return { type: 'redirect', location: `${location}${search}` };
        }
        const shared = DEFAULT_BRANCH_ONLY.find((entry) => entry.pattern.test(path));
        if (shared) {
            const params = new URLSearchParams(search);
            if (shared.branchParam && !params.has('branch')) params.set('branch', hostBranch);
            const query = params.toString();
            const target = regionalizePath(path, DEFAULT_BRANCH_CODE, hostBranch);
            return { type: 'redirect', location: `${target}${query ? `?${query}` : ''}` };
        }
        if (isFilePath(path) || PASSTHROUGH.some((pattern) => pattern.test(path))) {
            return { type: 'next', branch: hostBranch, publicHost: normalizedHost, publicPath: path };
        }
        return {
            type: 'rewrite',
            branch: hostBranch,
            publicHost: normalizedHost,
            publicPath: path,
            internalPath: `/geo/${hostBranch}${path === '/' ? '/' : path}`,
        };
    }

    if (normalizedHost && !isLocalHost(normalizedHost) && !getPublicHosts().includes(normalizedHost)) {
        return { type: 'notFound' };
    }

    if (geoMatch && isValidBranchCode(geoMatch[1]) && isHostRoutedBranch(geoMatch[1])) {
        const target = regionalizePath(geoMatch[2] || '/', geoMatch[1], DEFAULT_BRANCH_CODE);
        return { type: 'redirect', location: `${target}${search}` };
    }

    const resolved = resolveBranch({ host: normalizedHost, pathname: path });
    return { type: 'next', branch: resolved.code, publicHost: normalizedHost || getPublicHost(), publicPath: path };
}

/**
 * Внутренний путь режима path (/geo/<code>/x) -> публичная ссылка со страницы
 * филиала fromBranch. Нужен для целей SEO-редиректов на хосте филиала.
 */
export function publicLocationForInternalPath(path: string, fromBranch: string): string {
    if (!path.startsWith('/')) return path;
    const geo = path.match(/^\/geo\/([^/?#]+)(\/[^?#]*)?([?#].*)?$/);
    if (geo && isValidBranchCode(geo[1])) {
        return `${regionalizePath(geo[2] || '/', geo[1], fromBranch)}${geo[3] || ''}`;
    }
    const [pathname, rest = ''] = path.split(/(?=[?#])/, 2);
    return `${regionalizePath(pathname, DEFAULT_BRANCH_CODE, fromBranch)}${rest}`;
}

/**
 * Заголовок запроса, которым proxy.js просит next.config.js (rewrites.beforeFiles)
 * переписать путь хоста филиала на внутренний /geo/<code>/... Значение — код филиала.
 *
 * Почему не NextResponse.rewrite(): Next 16 нормализует хост rewrite-URL из proxy.js
 * в `localhost`, а роутер сравнивает его с `<proto>://<--hostname>:<port>`. При
 * `next start --hostname 127.0.0.1` (наш PM2) они не совпадают, Next считает rewrite
 * внешним, проксирует на https://localhost:<port> и отдаёт 500 (EPROTO) на всех
 * страницах msk.<домен>. Rewrite из next.config выполняется внутри роутера и от
 * origin не зависит. Входящее значение клиента proxy.js всегда удаляет.
 */
export const BRANCH_REWRITE_HEADER = 'x-branch-rewrite';
